import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, Observable, retry, throwError, timer } from "rxjs";
import { RouteType } from "src/app/models/enum";
import { BFF_SESSION_REQUIRED_MESSAGE, loginPathWithSessionUpgrade } from "src/session.config";
import { isAuthFailure, shouldSuppressAuthError } from "../utils/auth-session-policy.utils";
import { assignWindowLocation } from "../utils/browser-navigation.utils";
import { HttpErrorPayload, toHttpErrorPayload } from "../utils/http-error-message";

const MAX_NETWORK_RETRIES = 4;
const MAX_STAGING_POST_RETRIES = 3;
const BASE_RETRY_MS = 1000;

@Injectable({
	providedIn: "root",
})
export class HttpInterceptorService implements HttpInterceptor {
	constructor() {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const credentialedReq = req.clone({ withCredentials: true });
		const withErrorHandling = (source: Observable<HttpEvent<any>>) =>
			source.pipe(
				catchError((error: HttpErrorResponse) => {
					const payload = this.buildErrorPayload(error);
					this.maybeRedirectToLogin(payload);
					if (!shouldSuppressAuthError(payload)) {
						console.error(payload);
					}
					return throwError(() => payload);
				}),
			);

		if (credentialedReq.method === "GET") {
			return withErrorHandling(
				next.handle(credentialedReq).pipe(
					retry({
						count: MAX_NETWORK_RETRIES,
						delay: (error, retryCount) => this.retryDelay(error, retryCount, { statusZeroOnly: true }),
					}),
				),
			);
		}

		// Transient retries for draft staging push — avoids marking synced=false on a single blip.
		if (credentialedReq.method === "POST" && this.isStagingPost(credentialedReq.url)) {
			return withErrorHandling(
				next.handle(credentialedReq).pipe(
					retry({
						count: MAX_STAGING_POST_RETRIES,
						delay: (error, retryCount) =>
							this.retryDelay(error, retryCount, { statusZeroOnly: false, allowServerErrors: true }),
					}),
				),
			);
		}

		return withErrorHandling(next.handle(credentialedReq));
	}

	private isStagingPost(url: string): boolean {
		try {
			const path = url.includes("://") ? new URL(url).pathname : url;
			return path.replace(/\/$/, "").endsWith("/rating/staging");
		} catch {
			return /\/rating\/staging\/?$/.test(url);
		}
	}

	private retryDelay(
		error: unknown,
		retryCount: number,
		opts: { statusZeroOnly: boolean; allowServerErrors?: boolean },
	): Observable<number> {
		if (!(error instanceof HttpErrorResponse)) {
			return throwError(() => error);
		}

		const status = error.status;
		const retryable =
			status === 0 ||
			(!opts.statusZeroOnly && !!opts.allowServerErrors && (status === 502 || status === 503 || status === 504));

		if (!retryable) {
			return throwError(() => error);
		}

		const jitter = Math.floor(Math.random() * 250);
		const delayMs = Math.min(BASE_RETRY_MS * 2 ** (retryCount - 1) + jitter, 30000);
		return timer(delayMs);
	}

	private buildErrorPayload(error: HttpErrorResponse): HttpErrorPayload {
		const body = error?.error;
		const fromBody = toHttpErrorPayload(
			body && typeof body === "object"
				? { ...body, status: error.status }
				: { message: typeof body === "string" ? body : error.message, status: error.status },
		);

		const errors =
			fromBody.errors ??
			(body && Array.isArray(body.errors)
				? body.errors.filter((e: unknown): e is string => typeof e === "string")
				: undefined);

		const message =
			fromBody.message ??
			(typeof body?.message === "string" ? body.message : undefined) ??
			errors?.[0] ??
			(typeof error.message === "string" ? error.message : undefined);

		return {
			status: error.status ?? fromBody.status ?? 0,
			errors,
			message,
			code: fromBody.code,
			formNames: fromBody.formNames,
			raw: error,
		};
	}

	private maybeRedirectToLogin(payload: HttpErrorPayload): void {
		if (!navigator.onLine || payload.status === 0 || !isAuthFailure(payload)) return;
		if (this.isOnLoginPage()) return;
		const message = payload.message ?? payload.errors?.[0] ?? "";
		const loginPath =
			message === BFF_SESSION_REQUIRED_MESSAGE
				? loginPathWithSessionUpgrade(RouteType.LOGIN)
				: `/${RouteType.LOGIN}`;
		assignWindowLocation(loginPath);
	}

	private isOnLoginPage(): boolean {
		const path = window.location.pathname.replace(/\/$/, "");
		return path === `/${RouteType.LOGIN}` || path.endsWith(`/${RouteType.LOGIN}`);
	}
}
